import { useEffect, useMemo, useState } from 'react'
import type { DatedPoint, GraphWidgetOptions } from './types'
import type { GraphWidgetViewState } from './GraphWidgetView'

const DEFAULT_SYMBOL_ORDER = [
    'USDJPY', 'EURJPY', 'GBPJPY', 'AUDJPY', 'NZDJPY', 'CADJPY', 'CHFJPY',
    'AUDCAD', 'AUDNZD', 'NZDCAD',
    'EURUSD', 'GBPUSD', 'AUDUSD', 'NZDUSD', 'USDCAD', 'USDCHF',
    'XAUUSD', 'XAUJPY', 'XAGUSD',
    'VIX',
]

export type RatesSummaryViewProps = {
    title?: string
    theme?: GraphWidgetOptions['theme']
    state: GraphWidgetViewState
}

function useIsNarrow(breakpoint = 480): boolean {
    const [isNarrow, setIsNarrow] = useState(() =>
        typeof window !== 'undefined' ? window.innerWidth < breakpoint : false,
    )
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
        const mql = window.matchMedia(`(max-width: ${breakpoint - 1}px)`)
        const update = () => setIsNarrow(mql.matches)
        update()
        if (typeof mql.addEventListener === 'function') {
            mql.addEventListener('change', update)
            return () => mql.removeEventListener('change', update)
        }
        mql.addListener(update)
        return () => mql.removeListener(update)
    }, [breakpoint])
    return isNarrow
}

function useResolvedTheme(theme: GraphWidgetOptions['theme']): 'dark' | 'light' {
    const [resolved, setResolved] = useState<'dark' | 'light'>(() => {
        if (theme === 'dark' || theme === 'light') return theme
        const prefersDark =
            typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(prefers-color-scheme: dark)').matches
        return prefersDark ? 'dark' : 'light'
    })
    useEffect(() => {
        if (theme === 'dark' || theme === 'light') {
            setResolved(theme)
            return
        }
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            setResolved('light')
            return
        }
        const mql = window.matchMedia('(prefers-color-scheme: dark)')
        const update = () => setResolved(mql.matches ? 'dark' : 'light')
        update()
        if (typeof mql.addEventListener === 'function') {
            mql.addEventListener('change', update)
            return () => mql.removeEventListener('change', update)
        }
        mql.addListener(update)
        return () => mql.removeListener(update)
    }, [theme])
    return resolved
}

type SymbolRow = {
    symbol: string
    price: number | null
    pct: number | null
}

function extractPoints(raw: unknown): DatedPoint[] | null {
    if (!Array.isArray(raw) || raw.length === 0) return null
    const first = raw[0]
    if (typeof first === 'number') {
        return (raw as number[])
            .map((y) => (Number.isFinite(y) ? ({ y } as DatedPoint) : null))
            .filter((p): p is DatedPoint => p !== null)
    }
    if (typeof first === 'object' && first !== null) {
        return (raw as DatedPoint[]).filter((p) => p && typeof p.y === 'number' && Number.isFinite(p.y))
    }
    return null
}

function buildRow(symbol: string, raw: unknown): SymbolRow {
    const points = extractPoints(raw)
    if (!points || points.length === 0) {
        return { symbol, price: null, pct: null }
    }
    const firstY = points[0].y
    const lastY = points[points.length - 1].y
    const pct = firstY !== 0 && Number.isFinite(firstY) ? ((lastY - firstY) / firstY) * 100 : null
    return { symbol, price: lastY, pct }
}

const PRICE_DIGITS_OVERRIDE: Record<string, number> = {
    VIX: 2,
    XAUUSD: 2,
    XAUJPY: 0,
    XAGUSD: 3,
}

function formatPrice(symbol: string, value: number | null): string {
    if (value === null || !Number.isFinite(value)) return '-'
    const digits = PRICE_DIGITS_OVERRIDE[symbol] ?? (/JPY$/i.test(symbol) ? 3 : 5)
    return value.toFixed(digits)
}

function formatPct(value: number | null): string {
    if (value === null || !Number.isFinite(value)) return '-'
    const sign = value > 0 ? '+' : ''
    return `${sign}${value.toFixed(2)}%`
}

export function RatesSummaryView(props: RatesSummaryViewProps) {
    const { title, theme = 'auto', state } = props
    const resolvedTheme = useResolvedTheme(theme)
    const isDark = resolvedTheme === 'dark'
    const isNarrow = useIsNarrow()

    const rows = useMemo<SymbolRow[]>(() => {
        if (state.status !== 'success' || !state.data || !state.data.pairs) {
            return DEFAULT_SYMBOL_ORDER.map((symbol) => ({ symbol, price: null, pct: null }))
        }
        const pairs = state.data.pairs
        return DEFAULT_SYMBOL_ORDER.map((symbol) => buildRow(symbol, pairs[symbol]))
    }, [state])

    const palette = useMemo(() => ({
        up: isDark ? '#4ade80' : '#16a34a',
        down: isDark ? '#f87171' : '#dc2626',
        neutral: isDark ? 'rgba(226,232,240,0.65)' : '#6b7280',
        border: isDark ? 'rgba(148,163,184,0.18)' : '#e5e7eb',
        rowBorder: isDark ? 'rgba(148,163,184,0.10)' : '#f3f4f6',
        text: isDark ? '#e5e7eb' : '#111827',
        muted: isDark ? 'rgba(226,232,240,0.65)' : '#6b7280',
        panel: isDark ? '#0f172a' : '#ffffff',
        headerBg: isDark ? 'rgba(148,163,184,0.06)' : '#f9fafb',
    }), [isDark])

    const containerStyle: React.CSSProperties = {
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        fontFamily:
            'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
        border: `1px solid ${palette.border}`,
        borderRadius: 14,
        background: palette.panel,
        color: palette.text,
        overflow: 'hidden',
        marginTop: 0,
        marginRight: 0,
        marginLeft: 0,
        marginBottom: 16,
    }

    const titleStyle: React.CSSProperties = {
        fontSize: 13,
        fontWeight: 600,
        margin: 0,
        padding: '8px 10px',
        borderBottom: `1px solid ${palette.border}`,
    }

    const tableStyle: React.CSSProperties = {
        width: '100%',
        borderCollapse: 'collapse',
        fontVariantNumeric: 'tabular-nums',
        tableLayout: 'auto',
        margin: 0,
    }

    const cellBase = (isLast: boolean): React.CSSProperties => ({
        fontSize: 12,
        padding: '3px 10px',
        borderTop: 'none',
        borderRight: 'none',
        borderLeft: 'none',
        borderBottom: isLast ? 'none' : `1px solid ${palette.rowBorder}`,
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
    })

    const symbolCellStyle = (isLast: boolean): React.CSSProperties => ({
        ...cellBase(isLast),
        fontWeight: 600,
        width: 1,
    })

    const rightSymbolCellStyle = (isLast: boolean): React.CSSProperties => ({
        ...symbolCellStyle(isLast),
        borderLeft: `1px solid ${palette.rowBorder}`,
    })

    const priceCellStyle = (isLast: boolean): React.CSSProperties => ({
        ...cellBase(isLast),
        textAlign: 'right',
        fontSize: 14,
        fontWeight: 700,
        color: palette.text,
        width: '50%',
    })

    const pctCellStyle = (pct: number | null, isLast: boolean): React.CSSProperties => ({
        ...cellBase(isLast),
        textAlign: 'right',
        fontWeight: 600,
        width: 1,
        color: pct === null ? palette.neutral : pct > 0 ? palette.up : pct < 0 ? palette.down : palette.neutral,
    })

    // Left = クロス円 (7) + マイナー (3), Right = ドルストレート (6) + 貴金属 (3) + VIX.
    const splitIndex = 10
    const leftRows = rows.slice(0, splitIndex)
    const rightRows = rows.slice(splitIndex)
    const rowCount = Math.max(leftRows.length, rightRows.length)

    return (
        <div style={containerStyle}>
            {title ? <h3 style={titleStyle}>{title}</h3> : null}
            <table style={tableStyle}>
                <tbody>
                    {Array.from({ length: rowCount }).map((_, i) => {
                        const l = leftRows[i]
                        const r = rightRows[i]
                        const isLast = i === rowCount - 1
                        return (
                            <tr key={i}>
                                <td style={symbolCellStyle(isLast)}>{l?.symbol ?? ''}</td>
                                <td style={priceCellStyle(isLast)}>
                                    {l ? formatPrice(l.symbol, l.price) : ''}
                                </td>
                                {isNarrow ? null : (
                                    <td style={pctCellStyle(l?.pct ?? null, isLast)}>
                                        {l ? formatPct(l.pct) : ''}
                                    </td>
                                )}
                                <td style={rightSymbolCellStyle(isLast)}>{r?.symbol ?? ''}</td>
                                <td style={priceCellStyle(isLast)}>
                                    {r ? formatPrice(r.symbol, r.price) : ''}
                                </td>
                                {isNarrow ? null : (
                                    <td style={pctCellStyle(r?.pct ?? null, isLast)}>
                                        {r ? formatPct(r.pct) : ''}
                                    </td>
                                )}
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}
